import axios from "axios";
import { VisitOptions, Method } from "@inertiajs/core";
import { router } from "@inertiajs/react";
import { AttributeFilterParams, DateRangeFilterParams, DropDownValue, EventGroup } from "@/types";
import { EnhancedStore } from "@reduxjs/toolkit";
import { setGlobalError } from "@/store/core";
import { SavedFilter, SupportedContextType } from '@/types';

// Wrap inertia router visit in promise to enable async/await syntax
export async function asyncVisit(url: string, method: Method, options?: VisitOptions) {
    return new Promise((resolve, reject) => {
        const userOnSuccess = options?.onSuccess;
        const wrappedOptions: VisitOptions = {
            ...options,
            method,
            onSuccess: (page) => {
                userOnSuccess?.(page);
                resolve(page);
            },
            onError: (errors) => reject(errors),
        };
        router.visit(url, wrappedOptions);
    });
}

// Initialize global error handling for axios requests
export function initAxios(store: EnhancedStore) {
    axios.interceptors.response.use(
        (response) => response,
        (error) => {
            // Some errors don't have response (e.g. if a request was canceled it will raise CancelError)
            if (error.response) {
                switch (error.response.status) {
                    case 401:
                        // user is not logged in, navigate to login page
                        router.get(route('login'));
                        break;
                    case 419:
                        // Ask user to refresh the page
                        store.dispatch(setGlobalError({
                            message: 'The page has expired, please refresh and try again',
                            severity: 'warning',
                        }));
                        break;
                    default:
                        // Renders an error toast if needed
                        if (error.response.data.message) {
                            store.dispatch(setGlobalError({
                                message: error.response.data.message,
                                severity: 'error',
                            }));
                        }
                }
            }
            return Promise.reject(error);
        },
    );
}

// Raw AJAX API calls that don't use Inertia router
export async function fetchEventGroups() {
    return axios.get<EventGroup[]>(route('event-groups.raw-index'));
}

export async function createEventGroup(name: string) {
    return axios.post(route('event-groups.raw-store'), {
        name,
    });
}

export const fetchDistinctValues = async (field: string) => {
    return axios.get<string[]>(route('api.events.distinct-values', {field: field}));
}

export const fetchIebmsDistinctValues = async (field: string) => {
    return axios.get<string[]>(route('api.iebms-event.distinct-values', {field: field}));
}


export const fetchIebmsBookingsDistinctValues = async (field: string) => {
    return axios.get<string[]>(route('api.iebms-event.bookings.distinct-values', {field: field}));
}

export const fetchUserList = async () => {
    return axios.get(route('api.tag-list'));
}

export const fetchIebmsReportTemplateTags = async () => {
    return axios.get<string[]>(route('api.iebms-template.tags'));
}

export const fetchShDistinctValues = async (field: string) => {
    return axios.get<string[]>(route('api.sh-event.distinct-values', {field: field}));
}

export const fetchEventsFromApi = async (
    routeUrl: string,
    dateRangeFilter: DateRangeFilterParams,
    attributeFilter: AttributeFilterParams,
    page: number,
    size: number,
    groupId?: number,
    keywords?: string,
    sorting?: {
        sort_by: string,
        sort_dir: string,
    },
) => {
    const data = await axios.get(route(routeUrl, {...dateRangeFilter, ...attributeFilter, ...sorting, page: page, per_page: size, groupId: groupId, keywords }));
    // Only return events list
    return data.data.data;
}

export const fetchEventSubTypes = async () => {
    const params = new URLSearchParams();
    dependencyFieldValue.map(value => params.append(`${dependencyFieldId}[]`, value));
    return axios.get<string[]>(route('api.events.event-sub-types'), {params});
}

export const fetchIebmsTemplateTags = async () => {
    return axios.get<string[]>(route('api.iebms-template.tags'));
}

export const fetchIebmsTemplates = async () => {
    return axios.get<Array<{ id: number; name: string }>>(route('api.iebms-template.templates'));
}

// Saved Filters API functions
export interface SavedFiltersResponse {
    success: boolean;
    data: {
        user_filters: SavedFilter[];
        system_filters: SavedFilter[];
    };
}

export const fetchSavedFilters = async (contextType: SupportedContextType): Promise<SavedFiltersResponse> => {
    const response = await axios.get<SavedFiltersResponse>('/saved-filters/api', {
        params: { context_type: contextType },
    });
    return response.data;
};

export const createSavedFilter = async (data: {
    name: string;
    context_type: SupportedContextType;
    filter_attributes: any;
    table_view_options: any;
}): Promise<{ filter: SavedFilter; updatedFilters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] } }> => {
    const response = await axios.post<{
        success: boolean;
        message: string;
        filter_id: number;
        filter: SavedFilter;
        updated_filters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] };
    }>('/saved-filters/api', data);
    return {
        filter: response.data.filter,
        updatedFilters: response.data.updated_filters,
    };
};

export const shareSavedFilter = async (data: {
    context_type: SupportedContextType;
    filter_attributes: any;
    table_view_options: any;
    filter_id: number;
}): Promise<{
    filter: SavedFilter;
    updatedFilters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] };
}> => {
    const response = await axios.post<{
        success: boolean;
        message: string;
        filter: SavedFilter;
        updated_filters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] };
    }>('/saved-filters/api/share', data);
    return {
        filter: response.data.filter,
        updatedFilters: response.data.updated_filters,
    };
};

export const updateSavedFilter = async (
    id: number,
    data: {
        context_type: SupportedContextType;
        filter_attributes: any;
        table_view_options: any;
        name?: string;
    },
): Promise<{ filter: SavedFilter; updatedFilters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] } }> => {
    const response = await axios.put<{
        success: boolean;
        message: string;
        filter: SavedFilter;
        updated_filters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] };
    }>(`/saved-filters/api/${id}`, data);
    return {
        filter: response.data.filter,
        updatedFilters: response.data.updated_filters,
    };
};

export const deleteSavedFilter = async (id: number, contextType: SupportedContextType): Promise<{ updatedFilters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] } }> => {
    const response = await axios.delete<{
        success: boolean;
        message: string;
        updated_filters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] };
    }>(`/saved-filters/api/${id}?context_type=${contextType}`);
    return {
        updatedFilters: response.data.updated_filters,
    };
};

export const setDefaultSavedFilter = async (id: number, contextType: SupportedContextType): Promise<{ updatedFilters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] } }> => {
    const response = await axios.patch<{
        success: boolean;
        message: string;
        updated_filters: { user_filters: SavedFilter[]; system_filters: SavedFilter[] };
    }>(`/saved-filters/api/${id}/default?context_type=${contextType}`);
    return {
        updatedFilters: response.data.updated_filters,
    };
};

export const fetchImpactAnalysis = async (autoActionId: number, page = 1, perPage = 30, search = '') => {
    const params = new URLSearchParams();
    params.append('page', page.toString());
    params.append('per_page', perPage.toString());
    if (search) {
        params.append('search', search);
    }
    
    return axios.get(route('api.auto-actions.impact-analysis', autoActionId), { params });
}

export const triggerManualAutoAction = async (autoActionId: number) => {
    return axios.post(route('api.auto-actions.manual-trigger', autoActionId));
}

// Business units the logged in user may assign to a user
export const fetchUserBusinessUnits = async () => {
    return axios.get<DropDownValue[]>(route('api.users.business-units'));
}
